<template>
  <v-form ref="form" @submit.prevent>
    <FormSection>
      <BaseHeading :level="2">{{ $t(`${translationTicketPath}.engine.damage`) }}</BaseHeading>
    </FormSection>

    <div :class="$style['component-grid']">
      <FormSection v-for="(item, n) in getBikeSelections" :key="item.id" :class="$style.component">
        <h3>{{ item.name }}</h3>
        <BaseSelect
          v-model="formData.errorType[n]"
          :error-messages="chooseErrors('formData.errorType', n)"
          :hide-details="!chooseErrors('formData.errorType', n).length"
          class="mb-6"
          :label="$t('complaintForm.claim.error_type_label')"
          :items="getTranslatedErrorTypes(item.component)"
          required
          v-on="getTouchListeners('formData.errorType', n)"
        />

        <BaseSelect
          v-if="formData.errorType[n]"
          v-model="formData.detailedErrorType[n]"
          :error-messages="chooseErrors('formData.detailedErrorType', n)"
          :hide-details="!chooseErrors('formData.detailedErrorType', n).length"
          class="mb-6"
          :label="$t('complaintForm.claim.detailed_error_type_label')"
          :items="detailedErrorTypeItems(item.id)"
          required
          v-on="getTouchListeners('formData.detailedErrorType', n)"
        />

        <BaseTextfield
          v-model="formData.quantity[n]"
          class="mb-6"
          :placeholder="$t('complaintForm.claim.quantity')"
          :label="$t('complaintForm.claim.quantity')"
          required
          :error-messages="quantityRule(formData.quantity[n], n)"
        />

        <BaseTextarea
          v-model="formData.description[n]"
          rows="15"
          class="mb-6"
          :label="$t('complaintForm.claim.description')"
          counter
        />

        <v-row>
          <v-col class="py-0">
            <BaseHeading :level="3" class="mb-1">{{
              $t('complaintForm.claim.file_upload')
            }}</BaseHeading>

            <FileDropzone
              v-model="dropzoneFiles[n]"
              class="mb-6"
              :full-width="true"
              url="/api/bureturnapi?code=image-upload"
              required
              :options="{ createImageThumbnails: false, destroyDropzone: false }"
              @[onDropzoneSuccess]="res => onFileUploadSuccess(res, n)"
              @[onDropzoneSending]="setPending(true)"
              @[onDropzoneComplete]="setPending(false)"
              @[onDropzoneRemovedFile]="onFileUploadRemovedFile(n)"
            />
          </v-col>
        </v-row>
      </FormSection>
    </div>
    <ComplaintFormStepperControls
      :is-valid="isValid && validateAllQuantities && validateDetailedErrors"
      :has-required-fields="true"
      :prev-step="prevStep"
      :next-step="submitForm"
    />
  </v-form>
</template>

<script>
import { entries, get, isEmpty, isNull, cloneDeep, isUndefined, sortBy } from 'lodash-es'
import { mapGetters, mapMutations } from 'vuex'
import FORM_GETTER from '@/js/components/Forms/ComplaintForm/_store-module/getter-types.js'
import FORM_CONSTANTS from '@/js/components/Forms/ComplaintForm/_store-module/constants.js'
import StepperMixin from '@/js/mixins/stepper-mixin.js'
import ComplaintFormMixin from '@/js/components/Forms/ComplaintForm/_mixins/complaint-form-default-mixin.js'
import FormValidationMixin from '@/js/components/Forms/mixins/form-validation-mixin.js'
import { required } from 'vuelidate/lib/validators'

import FORM_MUTATION from '@/js/components/Forms/ComplaintForm/_store-module/mutation-types.js'

import FileDropzone, { FILE_DROPZONE_EVENTS } from '@/js/components/Shared/FileDropzone.vue'

import FormSection from '@/js/components/Forms/FormSection.vue'

import ComplaintFormStepperControls from '../../ComplaintFormStepperControls.vue'

const formDataEmpty = {
  id: [],
  errorType: [],
  detailedErrorType: [],
  quantity: [],
  description: [],
  fileUpload: [],
}

export default {
  components: {
    FormSection,
    FileDropzone,
    ComplaintFormStepperControls,
  },
  mixins: [FormValidationMixin, ComplaintFormMixin],
  props: {
    ...StepperMixin.props,
  },
  data() {
    return {
      onDropzoneSuccess: FILE_DROPZONE_EVENTS.VDROPZONE_SUCCESS,
      onDropzoneSending: FILE_DROPZONE_EVENTS.VDROPZONE_SENDING,
      onDropzoneComplete: FILE_DROPZONE_EVENTS.VDROPZONE_COMPLETE,
      onDropzoneRemovedFile: FILE_DROPZONE_EVENTS.VDROPZONE_REMOVED_FILE,
      dropzoneFiles: [],
      dropzoneErrors: [],
      formData: cloneDeep(formDataEmpty),
    }
  },
  validations: {
    formData: {
      errorType: {
        $each: {
          required,
        },
      },
      detailedErrorType: {
        $each: {
          required,
        },
      },
      quantity: {
        $each: {
          required,
        },
      },
    },
  },
  computed: {
    ...mapGetters(FORM_CONSTANTS.STORE_ID, {
      getBikeSelections: FORM_GETTER.COMPONENT_SELECTIONS,
    }),
    componentsToIndex() {
      return this.getBikeSelections.reduce((acc, item, index) => {
        return { ...acc, [item.id]: index }
      }, {})
    },
    componentsToIndexArray() {
      return sortBy(Object.entries(this.componentsToIndex), o => o[1])
    },
    /** Computed helper */
    partToKindTranslations() {
      return this.getMappedTranslations(
        'complaintForm.claim.map_part_to_kind',
        'complaintForm.claim.kind',
      )
    },
    // returns map_component_and_kind_to_error_detail matrix from the translations files
    detailedErrors() {
      /*
       example:
       "1": {
        "1": "[1,2]"
       }
       The first "1": is the Component, the Second the errorType
      */
      return this.getMappedDetailedErrors(
        'complaintForm.claim.map_component_and_kind_to_error_detail',
      )
    },
    // cachedFormDataErrorType() {
    //   return cloneDeep(this.formData.errorType)
    // },
    /** Form data */
    fullFormData() {
      const claim = []

      for (let index = 0; index < this.getBikeSelections.length; index++) {
        const bikeSelect = this.getBikeSelections[index]
        const translationIndex = this.mapHeaderToTranslationId(bikeSelect.component)

        const transKey = `complaintForm.claim.component.${translationIndex}`
        const transText = this.$t(transKey)
        const hierarchyString = transKey === transText ? bikeSelect?.hierarchy : undefined
        const hierarchyCode = isUndefined(hierarchyString)
          ? 'undefined'
          : hierarchyString.substring(0, 8)
        const hierarchyTransKey = `complaintForm.spare_parts_api_components.${hierarchyCode.toLowerCase()}`
        const hierarchyTransText = this.$t(hierarchyTransKey)
        const place =
          transKey !== transText
            ? transText
            : hierarchyTransKey !== hierarchyTransText
            ? hierarchyTransText
            : ''

        const component = {
          place,
          part: bikeSelect.name,
          itemNo: bikeSelect.itemNo,
          kind: `${this.$t(
            `complaintForm.claim.kind.${this.formData.errorType[index]}`,
          )} - ${this.$t(
            `complaintForm.claim.detailed_part_errors.${this.formData.detailedErrorType[index]}`,
          )}`,
          orderQuantity: this.formData.quantity[index],
          usedQuantity: bikeSelect.quantity,
          fileUpload: this.formData.fileUpload[index],
          description: this.formData.description[index],
        }

        claim.push(component)
      }

      return { claim }
    },
    cachedFormData() {
      return cloneDeep(this.formData)
    },
    /*
      if you change the errorType and set the actual detailedErrorType
      to undefined: - the validation doesnt trigger
    */
    validateDetailedErrors() {
      return this.cachedFormData.detailedErrorType.every(entry => !isUndefined(entry))
    },
    /*
      quantity is not part of the ValidationMixin. therefore this function exists
    */
    validateAllQuantities() {
      return this.formData.quantity
        .map((quantity, index) => this.quantityRule(quantity, index))
        .every(entry => typeof entry === 'object')
    },
  },
  watch: {
    componentsToIndex: {
      handler(newVal, oldVal) {
        this.formData = this.componentsToIndexArray.reduce((newFormData, [id]) => {
          const oldIndex = get(oldVal, id)
          if (isUndefined(oldIndex)) {
            newFormData.id.push(id)
            newFormData.errorType.push(undefined)
            newFormData.detailedErrorType.push(undefined)
            newFormData.quantity.push('1')
            newFormData.description.push(undefined)
            newFormData.fileUpload.push(undefined)
            return newFormData
          }

          newFormData.id.push(this.formData.id[oldIndex])
          newFormData.errorType.push(this.formData.errorType[oldIndex])
          newFormData.detailedErrorType.push(this.formData.detailedErrorType[oldIndex])
          newFormData.quantity.push(this.formData.quantity[oldIndex])
          newFormData.description.push(this.formData.description[oldIndex])
          newFormData.fileUpload.push(this.formData.fileUpload[oldIndex])
          return newFormData
        }, cloneDeep(formDataEmpty))
        this.dropzoneFiles = this.formData.fileUpload
      },
      immediate: true,
    },
  },
  methods: {
    ...mapMutations(FORM_CONSTANTS.STORE_ID, {
      updateFormData: FORM_MUTATION.UPDATE_FORM_DATA,
      setPending: FORM_MUTATION.SET_FORM_PENDING,
    }),
    quantityRule(quantity, index) {
      if (isEmpty(quantity) || isNaN(parseInt(quantity))) {
        quantity = 0
      }

      if (parseInt(quantity) < 1) {
        return this.$t('complaintForm.claim.quantity_min_error', {
          count: 1,
        })
      } else if (
        !isNull(this.getBikeSelections[index].quantity) &&
        parseInt(quantity) > this.getBikeSelections[index].quantity
      ) {
        return this.$t('complaintForm.claim.quantity_max_error', {
          count: this.getBikeSelections[index].quantity,
        })
      }
      return []
    },
    getMappedTranslations(mappingPath, translationPath) {
      const translations = {}
      const componentToPartMapping = this.$t(mappingPath)

      entries(componentToPartMapping).forEach(([key, value]) => {
        translations[key] = value
          .replace(/\[|\]/g, '')
          .split(',')
          .map(translationIndex => [
            translationIndex,
            this.$t(`${translationPath}.${translationIndex}`),
          ])
      })

      return translations
    },
    getMappedDetailedErrors(mappingPath) {
      const translations = {}
      const componentToPartMapping = this.$t(mappingPath)

      entries(componentToPartMapping).forEach(([key, value]) => {
        translations[key] = value
      })

      return translations
    },
    mapHeaderToTranslationId(header) {
      let id = ''
      switch (header) {
        case 'RAHMEN':
          id = '1'
          break
        case 'LAUFRAD':
          id = '2'
          break
        case 'SCHALTUNG':
          id = '3'
          break
        case 'BREMSE':
          id = '4'
          break
        case 'LENKER':
          id = '5'
          break
        case 'BLECHE':
          id = '6'
          break
        case 'SATTEL':
          id = '7'
          break
        case 'BELEUCHTUNG':
          id = '8'
          break
        case 'ZUBEHÖR':
          id = '10'
          break
        case 'DEKOR':
          id = '11'
          break
        case 'LACK':
          id = '12'
          break
        case 'BEILAGE':
          id = '13'
          break
        case 'SCHRAUBE':
          id = '14'
          break
        default:
          id = '0'
      }
      return id
    },
    getTranslatedErrorTypes(id) {
      return this.errorTypeItems(this.mapHeaderToTranslationId(id))
    },
    onFileUploadSuccess({ response }, index) {
      const fileUploadPath = get(response, 'files.0')

      if (fileUploadPath) {
        this.formData.fileUpload.splice(index, 1, fileUploadPath)
      } else {
        this.formData.fileUpload.splice(index, 1)
      }

      const fileUploadError = get(response, 'error')
        ? this.$t('form.dropzone.uploadFailed')
        : undefined

      if (fileUploadError) {
        this.dropzoneErrors.splice(index, 1, fileUploadError)
      } else {
        this.dropzoneErrors.splice(index, 1)
      }
    },
    onFileUploadRemovedFile(index) {
      if (index < this.getBikeSelections) {
        this.formData.fileUpload.splice(index, 1, undefined)
        this.dropzoneErrors.splice(index, 1)
      }
    },
    /** Select items */
    errorTypeItems(index) {
      return get(this.partToKindTranslations, index, []).map(([key, value]) => ({
        text: value,
        value: key,
      }))
    },
    detailedErrorTypeItems(itemNo) {
      const itemIndex = get(this.componentsToIndex, itemNo)
      const componentId = this.mapHeaderToTranslationId(this.getBikeSelections[itemIndex].component)
      const componentErrors = get(this.detailedErrors, componentId, [])
      const detailedErrors = get(componentErrors, this.formData.errorType[itemIndex], [])

      return detailedErrors
        .replace(/\[|\]/g, '')
        .split(',')
        .map(translationIndex => ({
          text: this.$t(`complaintForm.claim.detailed_part_errors.${translationIndex}`),
          value: translationIndex,
        }))
    },
    submitForm() {
      this.updateFormData(this.fullFormData)
      this.submit()
      if (this.isValid) {
        this.nextStep()
      }
    },
    /** Errors */
    chooseErrors(input, index) {
      return this.formErrorHelper(input, [this.chooseError], index)
    },
  },
}
</script>

<style lang="scss" module>
.component-grid {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    grid-template-columns: 1fr 1fr;

    section {
      margin-top: 0 !important;
    }
  }
  display: grid;
  grid-gap: 1rem;
  grid-template-columns: 1fr;
}

.component {
  position: relative;

  padding: 2.5rem 1.5rem;

  border: 1px solid var(--grey-lighten-1);
}

.remove {
  position: absolute;
  top: -1.25rem;
  right: 0.75rem;

  box-sizing: content-box;
  padding: 0.5rem 0.75rem;

  color: var(--red);

  background: var(--white);
}

.pointer,
.add,
.remove {
  cursor: pointer;
}

.info {
  color: var(--grey);
}
</style>
